import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import {
  User,
  Shield,
  ShieldCheck,
  ShieldOff,
  Key,
  Loader2
} from "lucide-react";

export default function Settings() {
  const navigate = useNavigate();
  const { token, user, updateUser } = useAuth();
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: ""
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [disableMFAOpen, setDisableMFAOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [disablingMFA, setDisablingMFA] = useState(false);

  const handleChangePassword = async () => {
    if (!passwords.current || !passwords.new) {
      toast.error("Please fill in all password fields");
      return;
    }
    if (passwords.new !== passwords.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (passwords.new.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwords.current,
          new_password: passwords.new
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password changed successfully");
      setPasswords({ current: "", new: "", confirm: "" });
    } catch (error) {
      console.error("Password change error:", error);
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDisableMFA = async () => {
    if (mfaCode.length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }

    setDisablingMFA(true);
    try {
      await axios.post(
        `${API}/auth/mfa/disable`,
        { temp_token: "", code: mfaCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser({ ...user, mfa_enabled: false });
      toast.success("MFA disabled successfully");
      setDisableMFAOpen(false);
      setMfaCode("");
    } catch (error) {
      console.error("MFA disable error:", error);
      toast.error(error.response?.data?.detail || "Invalid code");
      setMfaCode("");
    } finally {
      setDisablingMFA(false);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { label: "Super Admin", color: "bg-blue-100 text-blue-800" },
      main_office: { label: "Main Office", color: "bg-indigo-100 text-indigo-800" },
      location_admin: { label: "Location Admin", color: "bg-purple-100 text-purple-800" },
      staff: { label: "Staff", color: "bg-gray-100 text-gray-800" }
    };
    return badges[role] || badges.staff;
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">
          Manage your account settings and security
        </p>
      </div>

      {/* Profile Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-gray-400" />
            <CardTitle>Profile</CardTitle>
          </div>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-500">Name</Label>
              <p className="font-medium">{user?.name}</p>
            </div>
            <div>
              <Label className="text-gray-500">Email</Label>
              <p className="font-medium">{user?.email}</p>
            </div>
          </div>
          <div>
            <Label className="text-gray-500">Role</Label>
            <p className="mt-1">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRoleBadge(user?.role).color}`}>
                {getRoleBadge(user?.role).label}
              </span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Security Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-gray-400" />
            <CardTitle>Security</CardTitle>
          </div>
          <CardDescription>Manage your security settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* MFA Settings */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4">
              {user?.mfa_enabled ? (
                <div className="p-2 bg-green-100 rounded-lg">
                  <ShieldCheck className="h-5 w-5 text-green-600" />
                </div>
              ) : (
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <ShieldOff className="h-5 w-5 text-yellow-600" />
                </div>
              )}
              <div>
                <p className="font-medium">Two-Factor Authentication</p>
                <p className="text-sm text-gray-500">
                  {user?.mfa_enabled
                    ? "Your account is protected with MFA"
                    : "Add an extra layer of security"}
                </p>
              </div>
            </div>
            {user?.mfa_enabled ? (
              <Button
                variant="outline"
                onClick={() => setDisableMFAOpen(true)}
                data-testid="disable-mfa-btn"
              >
                Disable
              </Button>
            ) : (
              <Button
                onClick={() => navigate("/mfa-setup")}
                data-testid="enable-mfa-btn"
              >
                Enable
              </Button>
            )}
          </div>

          <Separator />

          {/* Change Password */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Key className="h-5 w-5 text-gray-400" />
              <h3 className="font-semibold">Change Password</h3>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current">Current Password</Label>
                <Input
                  id="current"
                  type="password"
                  value={passwords.current}
                  onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                  data-testid="current-password-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new">New Password</Label>
                  <Input
                    id="new"
                    type="password"
                    value={passwords.new}
                    onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">Confirm New Password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    value={passwords.confirm}
                    onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword}
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Changing...
                  </>
                ) : (
                  "Change Password"
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Disable MFA Dialog */}
      <Dialog open={disableMFAOpen} onOpenChange={setDisableMFAOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter your authenticator code to disable MFA. This will make your account less secure.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <InputOTP
              value={mfaCode}
              onChange={setMfaCode}
              maxLength={6}
              disabled={disablingMFA}
              data-testid="disable-mfa-code-input"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableMFAOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisableMFA}
              disabled={disablingMFA || mfaCode.length !== 6}
              data-testid="confirm-disable-mfa-btn"
            >
              {disablingMFA ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disabling...
                </>
              ) : (
                "Disable MFA"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
